import {
  memo,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { Button, Input, Typography, Divider, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  Trans,
  msg,
} from '@lingui/macro';
import { useLingui } from '@lingui/react';

import {
  // InputField, // TODO: Uncomment when action input fields are re-enabled
  ConnectionModal,
  JsonViewEdit,
} from '@components';

import NodeActionModal from './NodeActionModal';
import CodeNodeInputEditor from './CodeNodeInputEditor';
import IntegrationIcon from '../../integrations/IntegrationIcon';

import {
  useActiveOrganizationUuid,
  useSetNodeAction,
  useSetNodeConnection,
  useSetNodeInput,
  useSetNodeTitle,
  useDeleteNode,
  // useNodeActionInputFields, // TODO: Uncomment when backend endpoint is implemented
} from '@hooks';
import { useConnectionsQuery } from '../../../pages/ConnectionsPage/ConnectionsPage.query';
import { getEnvironmentColor, getEnvironmentLabel } from '../../../utils/environment';

import styles from './NodeConfiguration.module.scss';
import { useNodeResources } from './useNodeResources.query';
import {  NodeData } from '../../../types/node';

type NodeConfigurationProps = {
  data: NodeData;
};

const NodeConfiguration = ({ data }: NodeConfigurationProps) => {
  // If not editable, don't render anything (double safety, should already be hidden by NodeToolbar)
  if (!data.editable) {
    return null;
  }
  const { _ } = useLingui();
  const { data: nodeData } = useNodeResources();
  const { action_resources: nodeActions } = nodeData || {};
  const setNodeAction = useSetNodeAction(data);

  const organizationUuid = useActiveOrganizationUuid();
  const { data: connections, isLoading: isLoadingConnections } = useConnectionsQuery(organizationUuid);
  const setNodeConnection = useSetNodeConnection(data);
  const setNodeInput = useSetNodeInput(data);
  const setNodeTitle = useSetNodeTitle(data);
  const deleteNode = useDeleteNode(data);

  const { Text } = Typography;

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(data.title || '');

  // Sync local title state with data.title when it changes (e.g., after refetch)
  useEffect(() => {
    setTitleValue(data.title || '');
  }, [data.title]);

  // TODO: Uncomment when backend endpoint /api/v1/nodetypes/actions/{uuid}/input/ is implemented
  // const actionInputFields = useNodeActionInputFields(data.nodeAction);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isActionModalVisible, setIsActionModalVisible] = useState(false);
  const [showConnectionList, setShowConnectionList] = useState(false);

  // Get the selected connection object
  const selectedConnection = connections?.find(
    (conn: Connection) => conn.uuid === data.connection
  );

  // Filter connections based on search query
  const filteredConnections = useMemo(() => {
    if (!connections) return [];
    if (!searchQuery.trim()) return connections;

    const query = searchQuery.toLowerCase();
    return connections.filter((conn: Connection) =>
      conn.name.toLowerCase().includes(query) ||
      conn.nodetype_name?.toLowerCase().includes(query) ||
      conn.slug?.toLowerCase().includes(query) ||
      conn.environment?.toLowerCase().includes(query)
    );
  }, [connections, searchQuery]);

  const handleConnectionChange = async (connectionUuid: string) => {
    // Set the connection - backend will automatically set the integration
    await setNodeConnection(connectionUuid);
    // Close the connection list
    setShowConnectionList(false);
    // Open the action modal after selecting a connection
    setIsActionModalVisible(true);
  };

  const handleChangeConnection = () => {
    setShowConnectionList(true);
  };

  const handleChangeAction = () => {
    setIsActionModalVisible(true);
  };

  const handleSaveTitle = async () => {
    await setNodeTitle(titleValue);
    setIsEditingTitle(false);
  };

  const handleCancelTitle = () => {
    setTitleValue(data.title || '');
    setIsEditingTitle(false);
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalVisible(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalVisible(false);
  };

  const handleCloseActionModal = () => {
    setIsActionModalVisible(false);
  };

  const handleActionSelect = (actionUuid: string) => {
    setNodeAction(actionUuid);
    // Close the action modal but keep toolbar open
    setIsActionModalVisible(false);
  };

  // Filter actions for the selected connection's integration
  const filteredNodeActions = nodeActions?.filter((actionResource: NodeAction) => {
    return actionResource.node_type__uuid === data.nodeType?.uuid;
  }) || [];
  
  // Show connection list only if:
  // 1. User explicitly requested to change connection (showConnectionList = true), OR
  // 2. No connection is set on the node (data.connection is falsy), OR
  // 3. Connections are loaded but selected connection not found (data lost or connection deleted)
  const shouldShowConnectionList = showConnectionList || 
    !data.connection || 
    (!isLoadingConnections && !selectedConnection);

  return (
    <div className={styles.nodeConfigurationWrapper}>
      {isActionModalVisible ? (
        // Action Selection Modal
        <NodeActionModal
          onBack={handleCloseActionModal}
          connection={selectedConnection || null}
          nodeActions={filteredNodeActions}
          selectedActionUuid={data.nodeAction?.uuid}
          onActionSelect={handleActionSelect}
        />
      ) : shouldShowConnectionList ? (
        // Connection Selection View (initial state or when changing connection)
        <>
          {/* Connection List */}
          <div className={styles.inputFieldsTitle}>
            <Trans>Select connection</Trans>
          </div>
          <div className={styles.connectionListContainer}>
            <div className={styles.connectionList}>
              {filteredConnections?.map((connection: Connection) => (
                <div
                  key={connection.uuid}
                  className={`${styles.connectionListItem} ${
                    connection.uuid === data.connection ? styles.connectionListItemSelected : ''
                  }`}
                  onClick={() => handleConnectionChange(connection.uuid)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleConnectionChange(connection.uuid);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.connectionListIconContainer}>
                    <IntegrationIcon
                      iconUrl={connection.nodetype_icon}
                      slug={connection.nodetype_slug}
                      name={connection.nodetype_name || ''}
                      imageClassName={styles.connectionListIcon}
                    />
                  </div>
                  <div className={styles.connectionListInfo}>
                    <div className={styles.connectionListName}>{connection.name}</div>
                    <div className={styles.connectionListType}>{connection.nodetype_name}</div>
                    {(connection.environment || connection.slug) && (
                      <div className={styles.connectionListMeta}>
                        {connection.environment && (
                          <Tag color={getEnvironmentColor(connection.environment)}>
                            {_(getEnvironmentLabel(connection.environment))}
                          </Tag>
                        )}
                        {connection.slug && (
                          <span className={styles.connectionListSlug}>
                            <Trans>Slug:</Trans>{' '}
                            <code className={styles.connectionListSlugCode}>{connection.slug}</code>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed Footer with Search and Buttons */}
          <div className={styles.actionsFooter}>
            {/* Search Input */}
            <div className={styles.connectionSearchContainer}>
              <Input
                placeholder={_(msg`Search connections...`)}
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />
            </div>

            {/* Buttons Row */}
            <div className={styles.buttonsRow}>
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateModal}
                className={styles.createConnectionButton}
              >
                <Trans>Create new</Trans>
              </Button>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => deleteNode()}
                className={styles.deleteButton}
              >
                <Trans>Delete integration</Trans>
              </Button>
            </div>
          </div>
        </>
      ) : (
        // Configured View (when connection is selected, action optional)
        selectedConnection ? (
        <>
          {/* Selected Connection and Action Summary */}
          <div className={styles.configuredHeader}>
            {/* Node Title Section */}
            <div className={styles.configuredItem}>
              <Text className={styles.configuredLabel}>
                <Trans>Title</Trans>
              </Text>
              {isEditingTitle ? (
                <div className={styles.titleEditContainer}>
                  <Input
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    placeholder={_(msg`Enter custom title...`)}
                    onPressEnter={handleSaveTitle}
                  />
                  <div className={styles.titleButtonsRow}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleSaveTitle}
                    >
                      <Trans>Save</Trans>
                    </Button>
                    <Button
                      size="small"
                      onClick={handleCancelTitle}
                    >
                      <Trans>Cancel</Trans>
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={styles.selectedTitleCard}
                  onClick={() => setIsEditingTitle(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsEditingTitle(true);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Text className={styles.selectedTitleText}>
                    {data.title || <span className={styles.placeholderText}><Trans>Set custom title</Trans></span>}
                  </Text>
                  <EditOutlined className={styles.editIcon} />
                </div>
              )}
            </div>

            <Divider className={styles.divider} />

            <div className={styles.configuredItem}>
              <Text className={styles.configuredLabel}>
                <Trans>Connection</Trans>
              </Text>
              <div
                className={styles.selectedConnectionCard}
                onClick={handleChangeConnection}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleChangeConnection();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className={styles.selectedConnectionIconContainer}>
                  <IntegrationIcon
                    iconUrl={selectedConnection.nodetype_icon}
                    slug={selectedConnection.nodetype_slug}
                    name={selectedConnection.nodetype_name || ''}
                    imageClassName={styles.selectedConnectionIcon}
                  />
                </div>
                <div className={styles.selectedConnectionInfo}>
                  <div className={styles.selectedConnectionName}>
                    {selectedConnection.name}
                  </div>
                  <div className={styles.selectedConnectionType}>
                    {selectedConnection.nodetype_name}
                  </div>
                </div>
                <EditOutlined className={styles.editIcon} />
              </div>
            </div>

            <Divider className={styles.divider} />

            <div className={styles.configuredItem}>
              <Text className={styles.configuredLabel}>
                <Trans>Action</Trans>
              </Text>
              <div
                className={styles.selectedActionCard}
                onClick={handleChangeAction}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleChangeAction();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <Text className={styles.selectedActionName}>
                  {data.nodeAction?.name || <span className={styles.placeholderText}><Trans>Select action</Trans></span>}
                </Text>
                <EditOutlined className={styles.editIcon} />
              </div>
            </div>

            <Divider className={styles.divider} />

            {/* Node Input Section
              * For the Code nodetype's "Run Code" action we render a split
              * editor (code textarea + JSON for other fields) so users don't
              * have to wrap their Python in single quotes / hand-escape `\n`.
              * Hardcoded by action name as a Stage 1 stopgap; long-term the
              * dispatch should be driven by per-field metadata from the
              * backend (see useNodeActionInputFields TODO above).
              */}
            {(data.nodeAction?.name === 'Run Code' ||
              data.nodeAction?.name === 'List Run Code') ? (
              <CodeNodeInputEditor data={data} setNodeInput={setNodeInput} />
            ) : (
              <div className={styles.configuredItem}>
                <Text className={styles.configuredLabel}>
                  <Trans>Input (JSON)</Trans>
                </Text>
                <JsonViewEdit
                  value={data.input || null}
                  onChange={setNodeInput}
                  placeholder={_(msg`Enter JSON input data...`)}
                  minRows={5}
                  maxRows={15}
                />
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className={styles.actionsFooter}>
            <div className={styles.buttonsRow}>
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => deleteNode()}
                className={styles.deleteButton}
              >
                <Trans>Delete integration</Trans>
              </Button>
            </div>
          </div>
        </>
        ) : null
      )}

      {/* Connection Create Modal */}
      <ConnectionModal
        mode="create"
        visible={isCreateModalVisible}
        onClose={handleCloseCreateModal}
      />
    </div>
  );
};

const MemoizedNodeConfiguration = memo(NodeConfiguration);
export default MemoizedNodeConfiguration;
